import moment from 'moment';
import en from 'date-fns/locale/en-GB';
import { registerLocale } from 'react-datepicker';
import { MembershipType, Formats, PersonalizationSettings, PriceTier } from 'shovel-lib/types';
import t from '../i18n/t';
import storage from './storage';
import { DAYS_TO_START_AHEAD_MAX } from './constants/task';

export const isSubscription = type =>
  type === MembershipType.MONTHLY || type === MembershipType.SEMI_ANNUAL || type === MembershipType.ANNUAL;

const toDatepickerFormat = (format: string) => {
  return format.replace(/D/g, 'd').replace(/Y/g, 'y');
};

export const updateMomentLocale = (settings: PersonalizationSettings) => {
  const shortDate = settings.dateFormat!.replace(/^YYYY | YYYY$/g, '');
  const longDate = settings.dateFormat;
  const time = settings.time24Format ? 'HH:mm' : 'h:mma';
  const dayOfWeekShort = 'ddd';

  const momentFormats = {
    parentLocale: 'en',
    week: {
      dow: settings.firstDayOfWeek,
      doy: 7 + settings.firstDayOfWeek! - 1
    },
    longDateFormat: {
      [Formats.MONTH_DAY_FORMAT]: shortDate,
      [Formats.LONG_DATE]: longDate,
      [Formats.TIME]: time,
      [Formats.LOWERCASE_TIME]: time,
      [Formats.DATE_TIME_FORMAT]: `${shortDate}, ${time}`,
      [Formats.TASK_DATE_FORMAT]: `${shortDate}, ${time}`,
      [Formats.DAY_DATE_TIME_FORMAT]: `${dayOfWeekShort} ${shortDate}, ${time}`
    }
  };
  //@ts-ignore
  moment.locale('custom', momentFormats);
};

export const registerDatepickerLocale = (settings: PersonalizationSettings) => {
  const timeFormat = settings.time24Format ? 'HH:mm' : 'h:mmaaa';
  const dateFormat = toDatepickerFormat(settings.dateFormat!);
  const shortDateFormat = dateFormat.replace(/^yyyy | yyyy$/g, '');
  const mediumDateFormat = dateFormat.replace(/^yyyy|yyyy$/g, "''yy");
  const formats = {
    short: shortDateFormat,
    medium: dateFormat,
    long: `${shortDateFormat}, ${timeFormat}`,
    full: `${mediumDateFormat}, ${timeFormat}`
  };
  const customLocale = {
    ...en,
    formatLong: {
      date: ({ width }) => formats[width],
      time: () => timeFormat,
      dateTime: () => `${shortDateFormat}, ${timeFormat}`
    },
    options: {
      weekStartsOn: settings.firstDayOfWeek
    }
  };

  registerLocale('custom', customLocale);
};

export const deletingReasons = [
  { value: 1, label: t.DELETING_REASON1 },
  { value: 2, label: t.DELETING_REASON2 },
  { value: 3, label: t.DELETING_REASON3 },
  { value: 4, label: t.DELETING_REASON4 },
  { value: 5, label: t.DELETING_REASON5 },
  { value: 6, label: t.DELETING_REASON6 },
  { value: 7, label: t.DELETING_REASON7 },
  { value: 8, label: t.DELETING_REASON8 }
];

export const pricePerPlan = {
  [PriceTier.TIER_ONE]: {
    [MembershipType.MONTHLY]: 4,
    [MembershipType.ANNUAL]: 24,
    [MembershipType.LIFETIME]: 49.99
  },
  [PriceTier.TIER_TWO]: {
    [MembershipType.MONTHLY]: 2,
    [MembershipType.ANNUAL]: 9,
    [MembershipType.LIFETIME]: 19.99
  }
};

export const dealTextPerPlan = {
  [PriceTier.TIER_ONE]: {
    [MembershipType.MONTHLY]: '',
    [MembershipType.ANNUAL]: `50% ${t.LESS_THAN_MONTHLY}`,
    [MembershipType.LIFETIME]: t.MOST_POPULAR
  },
  [PriceTier.TIER_TWO]: {
    [MembershipType.MONTHLY]: '',
    [MembershipType.ANNUAL]: `63% ${t.LESS_THAN_MONTHLY}`,
    [MembershipType.LIFETIME]: t.MOST_POPULAR
  }
};

export function toPriceText(price: number) {
  return `$${price}`;
}

export function getPaymentSuccessLink(isParent: boolean, tier: PriceTier, type: MembershipType) {
  return `${isParent ? 'parent' : 'student'}-${tier === PriceTier.TIER_ONE ? 'reg' : 'pp'}-${type.toLowerCase()}`;
}

/**
 * Whether past planned blocks whose time isn't marked used yet show their ✓ disc. On unless the student turned it off: settings
 * saved before it existed have no key, and read as on.
 */
export const showsMarkUsedButtons = (settings?: { showMarkUsedButtons?: boolean } | null) =>
  settings?.showMarkUsedButtons !== false;

/**
 * Whether dropping a course or activity on the calendar opens the weekly repeat options. On unless the student turned it off:
 * settings saved before it existed have no key, and read as on.
 */
export const repeatsDroppedCommitments = (settings?: { repeatDroppedCommitments?: boolean } | null) =>
  settings?.repeatDroppedCommitments !== false;

/**
 * Whether Bumpers is on (timer spec "Bumpers"): a session dragged onto another takes its spot and bumps the rest down.
 * On for everyone unless turned off (Petr, 2026-10-19): settings saved before it existed have no key, and read as on.
 */
export const usesBumpers = (settings?: { bumpers?: boolean } | null) => settings?.bumpers !== false;

/**
 * The timer's chime and desktop notice when time's up (timer-alerts spec, Petr 2026-10-31). On unless turned off:
 * settings saved before they existed have no key, and read as on. The notice also needs the browser's say-so.
 */
export const playsTimerChime = (settings?: { timerChime?: boolean } | null) => settings?.timerChime !== false;
export const showsTimerNotice = (settings?: { timerNotice?: boolean } | null) => settings?.timerNotice !== false;

export type BumpersMode = 'bump' | 'jump';
/** As the menus list them: Jump first, the default (Petr, 2026-10-29). */
export const BUMPERS_MODES: BumpersMode[] = ['jump', 'bump'];

/**
 * What Bumpers does with the sessions a drag passes (Petr, 2026-10-28): Jump (the default) leaves them where they are;
 * Bump takes them along with it. Missed ones come along either way. Holding Shift does the other for one drag.
 */
export const bumpersMode = (settings?: { bumpersMode?: string } | null): BumpersMode =>
  settings?.bumpersMode === 'bump' ? 'bump' : 'jump';

/**
 * Whether the calendar still owes the student its one look at Bumpers (Petr, 2026-10-29: "once students get the version
 * with bumpers for the first time"): Bumpers on, not shown yet, and not before the first-run planner hint, so a new
 * account doesn't get both at once.
 */
export const bumpersIntroDue = (
  settings?: { bumpers?: boolean; bumpersIntroSeen?: boolean; onboardingStatus?: Record<string, boolean> } | null
) => !!settings && usesBumpers(settings) && !settings.bumpersIntroSeen && !!settings.onboardingStatus?.PLANNER_BAR;

/**
 * Whether free study time says "to study" under its length (Petr, 2026-10-10). On for new accounts, which get it saved
 * on (`NEW_ACCOUNT_SETTINGS`), so a new student learns what the shading is: time to study in, found by adding their
 * events. Off for accounts from before, which have no value saved, so their calendars don't change; anyone can switch it
 * in Settings.
 */
export const showsToStudyLabel = (settings?: { showToStudyLabel?: boolean } | null) => settings?.showToStudyLabel === true;

/**
 * Whether a new task's Start ahead comes from its time needed (Personalization, "Based on time needed") rather than the
 * same number of days for every task. The default for everyone (Petr, 2026-10-22): only a student who picked the days
 * ('days') gets them.
 */
export const startsAheadByTimeNeeded = (settings?: { startAheadMode?: string } | null) =>
  settings?.startAheadMode !== 'days';

export const DEFAULT_START_AHEAD_DAYS = 3;

/** A days-ahead value someone typed: a whole number from 0 (start on the due day) to the most a task can start ahead. */
export const isValidStartAheadDays = (value: unknown) => {
  if (value === null || value === undefined || String(value).trim() === '') return false;
  const days = Number(value);
  return Number.isInteger(days) && days >= 0 && days <= DAYS_TO_START_AHEAD_MAX;
};

/**
 * The student's days ahead, safe to put on a task: a value saved empty, as text or out of range (the settings popup
 * once saved an emptied box, and tasks got no Start ahead) reads as the default, 3, or the most allowed.
 */
export const startAheadDays = (value: unknown) => {
  if (value === null || value === undefined || String(value).trim() === '') return DEFAULT_START_AHEAD_DAYS;
  const days = Math.floor(Number(value));
  if (!Number.isFinite(days) || days < 0) return DEFAULT_START_AHEAD_DAYS;
  return Math.min(days, DAYS_TO_START_AHEAD_MAX);
};

/**
 * A new task's Start ahead once it has a due date. Based on time needed: the student's days, or a day for each hour if
 * that's more - time needed can raise it above their days, never lower it, so a short task doesn't start later than
 * they'd want (Petr, 2026-10-22). With no estimate yet, or the same days for every task: their days.
 */
export const startAheadFor = (estimateMinutes: number | undefined, daysAhead: unknown, byTimeNeeded: boolean) => {
  const days = startAheadDays(daysAhead);
  const minutes = Number(estimateMinutes) || 0;
  return byTimeNeeded && minutes > 0 ? Math.min(Math.max(days, Math.ceil(minutes / 60)), DAYS_TO_START_AHEAD_MAX) : days;
};

/**
 * Whether the Task List shows the student's own order ("Sort: By hand"), dragged into place, rather than by due date.
 * On the account, so it's the same on every device; settings without it read as by due date.
 */
export const sortsTaskListByHand = (settings?: { taskListSort?: string } | null) => settings?.taskListSort === 'hand';

/**
 * The column the Task List is sorted by, kept on the account (Petr: remembered after a reload), as the table takes it;
 * none, the list shows Due date or Hand. Saved as "priority" or "priority:desc".
 */
export const taskListColumnSort = (settings?: { taskListColumnSort?: string } | null): { id: string; desc: boolean }[] => {
  const saved = settings?.taskListColumnSort;
  if (typeof saved !== 'string' || !saved) return [];
  const [id, direction] = saved.split(':');
  return id ? [{ id, desc: direction === 'desc' }] : [];
};

export const columnSortSetting = (sortBy: { id: string; desc?: boolean }[]) =>
  sortBy[0] ? `${sortBy[0].id}${sortBy[0].desc ? ':desc' : ''}` : undefined;

/** What a new account's settings start with, on top of the usual defaults, when they are first saved. */
export const NEW_ACCOUNT_SETTINGS = { showToStudyLabel: true };

/**
 * Whether the study streak shows at the top of the calendar. It used to live only in this browser's storage, so settings
 * saved before it moved have no key and read what this browser says (on unless turned off there).
 */
export const showsStudyStreak = (settings?: { showStudyStreak?: boolean } | null): boolean =>
  settings?.showStudyStreak ?? storage.getShowStudyStreakOption();

/**
 * Whether the streak's last days show as bars beside the flame (only ever while the streak itself shows). On unless the
 * student turned them off: settings saved before they existed have no key, and read as on.
 */
export const showsStudyStreakDays = (settings?: { showStudyStreakDays?: boolean } | null) =>
  settings?.showStudyStreakDays !== false;

/**
 * Courses whose "Estimate tasks" box the student closed: it stays closed for them, and a course synced or imported later
 * brings it back for that course. Settings saved before this have no key: nothing closed.
 */
export const dismissedEstimateCourses = (settings?: { dismissedEstimateCourseIds?: unknown } | null) => {
  const ids = settings?.dismissedEstimateCourseIds;
  return new Set<number>(Array.isArray(ids) ? ids.filter((id): id is number => typeof id === 'number') : []);
};
